import React from "react";
import { joinClasses } from "../../utils/textUtils";

interface FormProps {
  onSubmit?: any;
  children: any;
  className?: string;
}

function Form(props: FormProps) {
  const { className, children, onSubmit } = props;

  return onSubmit ? (
    <form onSubmit={onSubmit} className={className}>
      {children}
    </form>
  ) : (
    <form onSubmit={(e) => e.preventDefault()} className={className}>
      {children}
    </form>
  );
}

const getStateClass = (state: any, value?: any) => {
  if (state) {
    if (state.error && state.error.condition) return "error";
    if (state.warning && state.warning.condition) return "warning";
    if (state.maxlength && value && state.maxlength.value === value.length)
      return "maxlength";
    if (state.success && state.success.condition) return "success";
    if (state.details && state.details.text) return "details";
  }

  return null;
};

const getStateText = (state: any, value?: any) => {
  const stateClass = getStateClass(state, value);
  if (stateClass !== null) {
    if (state && state[stateClass]) return state[stateClass].text;
  }
  return null;
};

interface InputSelectProps {
  type?: string;
  title?: any;
  placeholder?: string;
  required?: boolean;
  options?: any;
  state?: any;
  value?: any;
  onChange: any;
  disabled?: boolean;
  className?: string;
  noDetails?: boolean;
}

const InputSelect = (props: InputSelectProps) => {
  const {
    type,
    title,
    placeholder,
    required,
    options,
    state,
    value,
    onChange,
    disabled,
    className,
    noDetails,
  } = props;
  return (
    <label
      className={joinClasses([type, className, disabled ? "disabled" : null])}
    >
      {title ? (
        <span className="title">{`${title}${required ? " *" : ""}`}</span>
      ) : null}
      <select
        disabled={disabled}
        className={joinClasses([
          value === undefined ? "undefined" : null,
          getStateClass(state),
        ])}
        value={value === undefined ? true : value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option disabled value={undefined}>
          {placeholder}
        </option>
        )
        {options.map((item: any, index: number) => (
          <option key={index} disabled={item.disabled} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>
      {getStateClass(state) && !noDetails ? (
        <span className={joinClasses(["message", getStateClass(state, value)])}>
          {getStateText(state, value)}
        </span>
      ) : null}
    </label>
  );
};

interface InputTextProps {
  type?: string;
  autocomplete?: string;
  title?: any;
  placeholder?: string;
  required?: boolean;
  options?: any;
  state?: any;
  value?: any;
  onChange: any;
  onKeyEnter?: () => void;
  onKeyEscape?: () => void;
  disabled?: boolean;
  maxlength?: number;
  autofocus?: boolean;
  className?: string;
  min?: number;
  max?: number;
  noDetails?: boolean;
}

const InputText = (props: InputTextProps) => {
  const {
    type,
    autocomplete,
    title,
    required,
    placeholder,
    state,
    value,
    onChange,
    onKeyEnter,
    onKeyEscape,
    disabled,
    className,
    maxlength,
    autofocus,
    min,
    max,
    noDetails,
  } = props;

  return (
    <label
      className={joinClasses([type, className, disabled ? "disabled" : null])}
    >
      {title ? (
        <span className="title">{`${title}${required ? " *" : ""}`}</span>
      ) : null}
      <input
        type={type}
        autoComplete={autocomplete}
        min={type === "number" && min ? min : undefined}
        max={type === "number" && max ? max : undefined}
        maxLength={maxlength}
        autoFocus={autofocus}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && onKeyEnter) onKeyEnter();
          if (e.key === "Escape" && onKeyEscape) onKeyEscape();
        }}
        value={value}
        disabled={disabled}
        className={joinClasses([getStateClass(state)])}
      />
      {getStateClass(state) && !noDetails ? (
        <span className={joinClasses(["message", getStateClass(state)])}>
          {getStateText(state, value)}
        </span>
      ) : null}
    </label>
  );
};

interface InputTextareaProps {
  type?: string;
  title?: any;
  rows?: string | number;
  placeholder?: string;
  required?: boolean;
  state?: any;
  value?: any;
  onChange: any;
  disabled?: boolean;
  className?: string;
  noDetails?: boolean;
}

const InputTextarea = (props: InputTextareaProps) => {
  const {
    title,
    required,
    rows,
    placeholder,
    state,
    value,
    onChange,
    disabled,
    className,
    noDetails,
  } = props;

  const stateClass = `${getStateClass(state, value)}`.replace(
    "maxlength",
    "warning"
  );

  return (
    <label
      className={joinClasses([
        "textarea",
        className,
        disabled ? "disabled" : null,
      ])}
    >
      {title ? (
        <span className="title">{`${title}${required ? " *" : ""}`}</span>
      ) : null}
      <textarea
        required={required}
        rows={Number(rows)}
        maxLength={state.maxlength ? state.maxlength.value : 1000}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        value={value}
        disabled={disabled}
        className={stateClass}
      />
      {getStateClass(state) && !noDetails ? (
        <span className={joinClasses(["message", stateClass])}>
          {getStateText(state, value)}
        </span>
      ) : null}
    </label>
  );
};

interface InputCheckboxProps {
  type?: string;
  label?: any;
  value?: any;
  onChange: any;
  disabled?: boolean;
  className?: string;
}

const InputCheckbox = (props: InputCheckboxProps) => {
  const { type, label, value, onChange, disabled, className } = props;
  return (
    <label className={joinClasses([type, className])}>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span>{label}</span>
    </label>
  );
};

interface InputRadioProps {
  type?: string;
  labels?: any[];
  name?: string;
  onChange: any;
  className?: string;
}

const InputRadio = (props: InputRadioProps) => {
  const { type, labels, name, onChange, className } = props;
  if (labels && labels.length)
    return (
      <>
        {labels.map((item: any, index: number) => (
          <label key={index} className={joinClasses([type, className])}>
            <input
              type="radio"
              name={name}
              value={item.value}
              disabled={item.disabled}
              onChange={(e) => onChange(e.target.value)}
            />
            <span>{item.text}</span>
          </label>
        ))}
      </>
    );

  return null;
};

interface InputProps
  extends InputSelectProps,
    InputTextProps,
    InputTextareaProps,
    InputCheckboxProps,
    InputRadioProps {}

function Input(props: InputProps) {
  const { type } = props;

  const content = () => {
    switch (type) {
      case "switch":
      case "checkbox":
        return <InputCheckbox {...props} />;
      case "radio":
        return <InputRadio {...props} />;
      case "text":
      case "number":
      case "datetime-local":
      case "password":
        return <InputText {...props} />;
      case "textarea":
        return <InputTextarea {...props} />;
      case "select":
        return <InputSelect {...props} />;

      default:
        return null;
    }
  };

  return content();
}

export { Form, Input };
